import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import * as moment from 'moment';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { Router } from '@angular/router/';
import { VesselImage } from 'anchor-shared/lib/model/entity/vesselImage';
import { AppSwiperCarouselImage } from 'anchor-shared/lib/model/interface/carouselImage';
import { VesselImageDAO } from '../../../core/services/dao/vesselImageDAO';
import { OrderVesselImagesByPipe } from '../../shared/pipes/order-vessel-images-by.pipe';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { CaptainDAO } from '../../../core/services/dao/captainDAO';
import { CustomerCaptainPickerService } from '../../shared/dialogs/customer-captain-picker/customer-captain-picker-service';
import { PaymentDAO } from '../../../core/services/dao/paymentDAO';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { CustomerBoatRequestDialogService } from '../../shared/dialogs/customer-boat-request-dialog/customer-boat-request-dialog.service';
import { AppLocation } from '../../../core/models/interface/geo';
import { CustomerPricingDialogService } from '../../shared/dialogs/customer-pricing-dialog/customer-pricing-dialog.service';
import { CustomerCharter } from '../../portal/charters/charter-payment/charter-payment.component';
import { CustomerCaptain } from 'anchor-shared/lib/model/interface/customerInterfaces';

import { addNewLines } from '../../portal/charters/manage-charter/manage-charter.component';
import { NumberFormatterService } from '../../../core/services/number.formatter.service';
import { CustomerDAO } from '../../../core/services/dao/customerDAO';
import { DialogsService } from '../../../core/services/dialog.service';
import { getAddOnsTotalFromPricingMetaData } from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { CustomerPaymentCharterDetails } from 'anchor-shared/lib/model/interface/depostitInterfaces';
import { PaymentType } from 'anchor-shared/lib/model/enum/payment-type';

@Component({
  selector: 'app-customer-charter-details',
  templateUrl: './customer-charter-details.component.html',
  styleUrls: ['./customer-charter-details.component.scss'],
})
export class CustomerCharterDetailsComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  charterId: number;
  charter: CustomerCharter;
  charterStatus: string;
  formattedCharterDate: string;
  subs: Subscription[] = [];
  passengerNotes: string;
  vesselImages: VesselImage[] = [];
  carouselItems: AppSwiperCarouselImage[] = [];
  carouselItemsLoaded = false;
  chosenCaptain: CustomerCaptain;
  preferredCaptains: CustomerCaptain[] = [];
  boat: Vessel;
  primaryPayment: Payment;
  remainingPayment: Payment;
  payments: Payment[];
  depositTransactionDate: string;
  remainingTransactionDueDate: string;
  remainingTransactionPaidDate: string;
  formattedAgreementDate: string;
  formattedAgreementSignedDate: string;
  withinFullPaymentThreshold: boolean;
  parkingLocation: AppLocation;
  mapsLink: string;
  charterDetails: CustomerPaymentCharterDetails;
  captainChosen = false;
  parkingInstructions: string;
  instructionsAndRules: string;
  endDate: string;
  promoAmount: number = 0;
  captainStatus: string = 'Pending approval...';
  captainClass: string = 'orange-status';
  addOnsTotal: number = 0;

  constructor(
    private charterDAO: CharterDAO,
    private vesselImageDAO: VesselImageDAO,
    private captainDAO: CaptainDAO,
    private paymentDAO: PaymentDAO,
    private CustomerDAO: CustomerDAO,
    private route: ActivatedRoute,
    public router: Router,
    private captainPickerDialog: CustomerCaptainPickerService,
    private orderVesselImagesByPipe: OrderVesselImagesByPipe,
    private boatDeliveryDialog: CustomerBoatRequestDialogService,
    private charterPricingModal: CustomerPricingDialogService,
    private numFormatter: NumberFormatterService,
    private dialogService: DialogsService,
  ) {
    this.parkingLocation = {
      position: {
        lat: 27.964157,
        lng: -82.452606,
      },
      addressComponents: [],
      formattedAddress: '',
      vicinity: '',
      markerLatitude: 0,
      markerLongitude: 0,
      zoom: 8,
    };
  }

  ngOnInit() {
    this.charterId = this.route.snapshot.params.charterId;
    const charterSub = this.charterDAO
      .getCustomerCharterDetails(this.charterId)
      .subscribe((charter) => {
        this.charter = charter;
        this.initCharterStatus(charter);
        this.initMapAddress();
        this.charterWithinSevenDayThreshold();
        this.initCaptainToDisplay();
        this.initPromoCheck();
        this.initCaptainStatusCheck();

        this.mapsLink =
          'https://www.google.com/maps/search/?api=1&query=' +
          this.charter.boat.mooringLocation.parkingLat +
          ',' +
          this.charter.boat.mooringLocation.parkingLong;

        this.formattedCharterDate = moment(this.charter.date).format(
          'dddd, MMMM D, YYYY',
        );

        this.endDate = this.setEndDate(
          this.formattedCharterDate,
          this.charter.startTime,
          this.charter.duration,
        );

        if (this.charter.agreementCreatedDate) {
          this.formattedAgreementDate = moment(
            this.charter.agreementCreatedDate,
          ).format('dddd, MMMM D, YYYY · h:mm A');
        }
        if (this.charter.agreementSignedDate) {
          this.formattedAgreementSignedDate = moment(
            this.charter.agreementSignedDate,
          ).format('dddd, MMMM D, YYYY · h:mm A');
        }

        this.initPaymentDetails();

        const charterItin = this.charter.itinerary;
        if (charterItin) {
          if (charterItin.passengerNotes) {
            this.passengerNotes = addNewLines(charterItin.passengerNotes);
          }
          if (charterItin.parkingInstructions) {
            this.parkingInstructions = addNewLines(
              charterItin.parkingInstructions,
            );
          }
          if (charterItin.rulesOfTheBoat) {
            this.instructionsAndRules = addNewLines(charterItin.rulesOfTheBoat);
          }
        }
        const imageSub = this.vesselImageDAO
          .getCroppedByVesselId(this.charter.vesselId)
          .subscribe((vesselImages) => {
            this.vesselImages = this.orderVesselImagesByPipe.transform(
              vesselImages,
              'sortOrder',
            );
            this.createCarouselList();
          });
        this.subs.push(imageSub);
      });
    this.subs.push(charterSub);
  }

  signAgreementClick(): void {
    this.router.navigate(['customer/charter/' + this.charterId + '/agreement']);
  }

  private initCaptainToDisplay(): void {
    const captIds = this.charter.boat.vesselCaptainMap.map((item) => {
      return item.captainId;
    });
    const activeCaptSub = this.captainDAO
      .loadCustomerCaptainsForVessel(captIds)
      .subscribe((preferred) => {
        this.preferredCaptains = preferred;
        this.setActiveCaptainState();
      });
    this.subs.push(activeCaptSub);
  }

  private initCharterStatus(charter: CustomerCharter): void {
    if (!charter.cancelled) {
      if (!charter.completed) {
        if (charter.availabilityStatus !== 'Available') {
          this.charterStatus = 'Pending Approval';
        } else if (!charter.paymentReceived) {
          this.charterStatus = 'Awaiting payment';
        } else if (charter.paymentReceived && isBeforeCharterMoment(charter)) {
          this.charterStatus = 'Ready to Set Sail';
        } else if (charter.paymentReceived && !isBeforeCharterMoment(charter)) {
          this.charterStatus = 'Completed';
        }
      } else {
        this.charterStatus = 'Completed';
      }
    } else {
      this.charterStatus = 'Cancelled';
    }
  }

  private createCarouselList() {
    this.vesselImages.forEach((item) => {
      this.carouselItems.push({
        swiperImageUrl: item.url,
        getSwiperImageUrl(): string {
          return this.swiperImageUrl;
        },
      });
    });
    this.carouselItemsLoaded = true;
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  openCaptainPicker() {
    this.captainPickerDialog
      .open(
        this.preferredCaptains,
        this.charterId,
        this.charter.boat.primaryCaptainId,
      )
      .subscribe((data) => {
        console.log(data);
        if (data) {
          this.chosenCaptain = data;
          this.charter.captainIdRequestedByCustomer = this.chosenCaptain.id;
          this.captainChosen = true;
        }
      });
  }

  private initPaymentDetails() {
    const paymentsSub = this.paymentDAO
      .getPaymentsByCharterId(this.charterId)
      .subscribe((payments) => {
        this.payments = payments;
        this.primaryPayment = this.payments.find((payment) => {
          return payment.type !== PaymentType.REMAINING;
        });
        this.remainingPayment = this.payments.find((payment) => {
          return payment.type === PaymentType.REMAINING;
        });
        if (this.primaryPayment) {
          if (
            this.primaryPayment.transactionCreatedAt &&
            this.primaryPayment.paid
          ) {
            this.depositTransactionDate = moment(
              this.primaryPayment.transactionCreatedAt,
            ).format('dddd, MMMM D, YYYY');
            this.remainingTransactionDueDate = moment(this.charter.date)
              .startOf('day')
              .subtract(7, 'days')
              .format('dddd, MMMM D, YYYY');
          }
          if (this.remainingPayment && this.remainingPayment.paid) {
            this.remainingTransactionPaidDate = moment(
              this.remainingPayment.transactionCreatedAt,
            ).format('dddd, MMMM D, YYYY');
          }
          this.paymentDAO
            .getCharterDetails(this.primaryPayment.id)
            .subscribe((details) => {
              this.charterDetails = details;
              this.charterDetails.discounts =
                this.numFormatter.parseArrayFromJson(this.charter.discounts);
              this.charterDetails.services =
                this.numFormatter.parseArrayFromJson(this.charter.services);

              this.initAddOns();
            });
        }
      });
    this.subs.push(paymentsSub);
  }

  goToCharterPayment() {
    const customerSub = this.CustomerDAO.getSessionToken().subscribe(
      (session) => {
        this.router.navigate([
          `/charters/${this.charterId}/payments/${this.primaryPayment.id}/${session.sessionToken}`,
        ]);
      },
    );
    this.subs.push(customerSub);
  }

  viewCharterAgreement() {
    window.open(this.charter.signedAgreementUrl);
  }

  charterWithinSevenDayThreshold(): void {
    this.withinFullPaymentThreshold =
      moment().startOf('day') >=
      moment(this.charter.date).startOf('day').subtract(7, 'days');
  }

  openBoatDeliveryModal() {
    this.boatDeliveryDialog.open(this.charterId).subscribe();
  }

  private initMapAddress() {
    const mooringLocation = this.charter.boat.mooringLocation;
    if (mooringLocation.parkingLat && mooringLocation.parkingLong) {
      this.parkingLocation.markerLatitude = mooringLocation.parkingLat;
      this.parkingLocation.markerLongitude = mooringLocation.parkingLong;
      this.parkingLocation.mapLatitude = mooringLocation.parkingLat;
      this.parkingLocation.mapLongitude = mooringLocation.parkingLong;
      this.parkingLocation.zoom = 15;
    }
  }

  openPricingDetails() {
    const pricingModalSub = this.charterPricingModal
      .open(this.charterDetails)
      .subscribe();
    this.subs.push(pricingModalSub);
  }

  private setActiveCaptainState(): void {
    if (
      this.charter.captainIdRequestedByCustomer &&
      this.charter.captain &&
      this.charter.captain.id === null
    ) {
      this.preferredCaptains.forEach((preferredCaptain) => {
        if (preferredCaptain.id === this.charter.captainIdRequestedByCustomer) {
          this.chosenCaptain = preferredCaptain;
        }
      });
    } else if (this.charter.captain && this.charter.captain.id) {
      this.chosenCaptain = this.charter.captain;
    } else if (this.charter.boat.primaryCaptainId) {
      this.preferredCaptains.forEach((preferredCaptain) => {
        if (preferredCaptain.id === this.charter.boat.primaryCaptainId) {
          this.chosenCaptain = preferredCaptain;
        }
      });
    } else {
      this.chosenCaptain = this.preferredCaptains[0];
    }
  }

  setEndDate(date, time, duration) {
    let dateTime = moment(date + ' ' + time, 'ddd MMM D, YYYY H:mm a')
      .add(duration, 'hours')
      .format('ddd MMM D, YYYY');
    return dateTime;
  }

  private initPromoCheck() {
    if (this.charter && this.charter.promoCodeText) {
      this.promoAmount = this.charter.promoCodeTotal;
    }
  }

  private initCaptainStatusCheck() {
    if (
      !this.charter.captainIdRequestedByCustomer &&
      !this.charter.completed &&
      !this.charter.captain.id
    ) {
      this.captainClass = 'orange-status';
      this.captainStatus = 'Awaiting approval';
    } else if (
      this.charter.captainIdRequestedByCustomer &&
      !this.charter.captain.id
    ) {
      this.captainClass = 'orange-status';
      this.captainStatus = 'Pending approval';
    } else if (this.charter.captain.id && !this.charter.completed) {
      this.captainClass = 'ccd-green';
      this.captainStatus = this.charter.captain.phoneNumber;
    }
  }

  private initAddOns() {
    this.addOnsTotal = getAddOnsTotalFromPricingMetaData(
      this.charterDetails.pricingMetaData,
      null,
      true,
    );
  }

  openDepositInfoModal() {
    this.dialogService.HelpDialog(
      `A hold (not a charge) will be placed on your credit card 48 hours before your booking starts to cover any incidental damage that may occur during your rental. This hold is released 48 hours after the booking is complete,
      if no claims are made.Deposit amountThis owner requires a $${this.charter.depositAmount} security deposit. A hold (not a charge) for $${this.charter.depositAmount} will be placed on your credit card 48 hours before your departure date`,
      'Security Deposit',
      this.charter.depositAmount.toString(),
    );
  }
}

export function isBeforeCharterMoment(charter: CustomerCharter): boolean {
  const now = moment();
  const charterDateTime = moment(
    charter.date + ' ' + charter.charterEndTime,
    'YYYY-MM-DD hh:mm:a',
  );
  return now.isBefore(charterDateTime);
}
